import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useDebugOverrideAddress } from "@/hooks/user/useDebugOverrideAddress";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";

/**
 * Hook to fetch spinning wheel history for the authenticated user
 * Returns all past spin results with their rewards
 * Supports admin address override for debugging using the unified useDebugOverrideAddress hook
 */
export const useSpinningWheelHistory = () => {
  const { hubUser, auth } = useHubUser();
  const apiHelper = useApi();

  /**
   * Use the unified debug override address hook
   * This allows admins to view spinning wheel history for other users
   */
  const queryAddress = useDebugOverrideAddress();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [QueryKeys.SPINNING_WHEEL_RESULTS, hubUser?.id, queryAddress],
    queryFn: async () => {
      if (!queryAddress) return;
      const result = await apiHelper.getWithAuth(auth, API_ENDPOINT.SPINNING_WHEEL_RESULTS, {
        queryParams: { defiAddress: queryAddress },
      });
      return result;
    },
    enabled: !!hubUser?.id,
    staleTime: 30 * 1000, // 30 seconds
    retry: 2,
  });

  return {
    results: data?.results ?? [],
    isLoading,
    error: error?.message ?? null,
    refetch,
  };
};
